import moment from 'moment';
import { CancellationPolicyType } from 'shared-types';
import { OrderItemType, OrderType } from '../schemas';
import { some } from '@utils/some';

export type MappedOrderItem = {
  id: number;
  name: string;
  checkIn: string;
  checkOut: string | null;
  cancellationPolicy: CancellationPolicyType | null;
  adults: number | null;
  children: number | null;
  guests?: number;
  extras: string[];
  rate: string | null;
  type: string;
  price: string;
  dynamicPackageItemType: string | null;
  packageParentId: number | null;
  status: string | null;
};

function mapRoomsDetails(item: OrderItemType): MappedOrderItem {
  const extrasTotal =
    item.bookable?.room_extras.reduce((acc, e) => {
      if (typeof e !== 'string') acc += e.price?.amount || 0;
      return acc;
    }, 0) || 0;
  const totalPrice = (item.bookable?.price?.amount || 0) + extrasTotal;
  return {
    id: item.id,
    name: item.bookable?.name || '',
    checkIn: `${moment(item.check_in).format('DD MMM YYYY')}`,
    checkOut: `${moment(item.check_out).format('DD MMM YYYY')}`,
    cancellationPolicy: item.bookable?.cancellation_policy || null,
    adults: item.adults,
    children: item.children,
    extras: item.bookable?.room_extras.map((extra) => extraToDisplayString(extra)) ?? [],
    rate: item.bookable?.rate || null,
    price: item.bookable?.price?.amount
      ? `${item.bookable.price.prefix}${(totalPrice / 100).toFixed(2)}`
      : '',
    type: 'rooms',
    dynamicPackageItemType: item.dynamic_package,
    packageParentId: item.package_order_item_id,
    status: item.status,
  };
}

function mapTablesDetails(item: OrderItemType, diningLabel?: string): MappedOrderItem {
  return {
    id: item.id,
    name: diningLabel || 'Dining Reservation',
    checkIn: `${moment(item.check_in).format('DD MMM YYYY HH:mm')}`,
    checkOut: null,
    cancellationPolicy: item.bookable?.cancellation_policy || null,
    adults: item.adults,
    children: item.children,
    extras: item.bookable?.room_extras.map((extra) => extraToDisplayString(extra)) ?? [],
    rate: item.bookable?.ids?.restaurant_name || null,
    price: item.bookable?.price?.amount
      ? `${item.bookable.price.prefix}${(item.bookable.price.amount / 100).toFixed(2)}`
      : '',
    type: 'tables',
    dynamicPackageItemType: item.dynamic_package,
    packageParentId: item.package_order_item_id,
    status: item.status,
  };
}

function mapSpaDetails(item: OrderItemType): MappedOrderItem {
  const extraSelections =
    item.type === 'spa-packages'
      ? item.selection_groups
          ?.filter((group) => group.is_extras)
          .flatMap((group) => group.selections)
      : item.guest_details?.filter((g) => some(g.extras)).flatMap((g) => g.extras);
  const guests = item.guest_details?.length || 0;

  let totalPrice =
    item.bookable?.price?.amount && guests > 1
      ? item.bookable?.price?.amount * guests
      : item.bookable?.price?.amount;

  if (item.type === 'spa-packages') {
    totalPrice =
      (totalPrice || 0) +
      (extraSelections || []).reduce((acc, e) => {
        acc += e.price?.amount || 0;
        return acc;
      }, 0);
  }
  if (item.type === 'spa-treatments' || item.type === 'spa-facilities') {
    totalPrice =
      (totalPrice || 0) +
      (item.guest_details || []).reduce((acc, guest) => {
        acc += (guest.extras || []).reduce((acc, e) => {
          acc += e.price?.amount || 0;
          return acc;
        }, 0);
        return acc;
      }, 0);
  }

  return {
    id: item.id,
    name: item.bookable?.name || '',
    checkIn: `${moment(item.check_in).format('DD MMM YYYY HH:mm')}`,
    checkOut: null,
    cancellationPolicy: item.bookable?.cancellation_policy || null,
    adults: item.adults,
    children: item.children,
    guests,
    extras: extraSelections?.map((group) => extraToDisplayString(group)) || [],
    rate: null,
    price: totalPrice ? `${item.bookable?.price?.prefix}${(totalPrice / 100).toFixed(2)}` : '',
    type: 'spa',
    dynamicPackageItemType: item.dynamic_package,
    packageParentId: item.package_order_item_id,
    status: item.status,
  };
}

export function mapOrderDetails(
  order: OrderType,
  options?: { diningLabel?: string }
): MappedOrderItem[] {
  const rooms = order.items
    .filter((item) => item.type === 'rooms')
    .map((item) => mapRoomsDetails(item));
  const tables = order.items
    .filter((item) => item.type === 'tables')
    .map((item) => mapTablesDetails(item, options?.diningLabel));
  const spa = order.items
    .filter((item) => item.type.includes('spa'))
    .map((item) => mapSpaDetails(item));
  return [...rooms, ...tables, ...spa];
}

const extraToDisplayString = (extra: string | { name: string; quantity: number }): string => {
  return typeof extra === 'string' ? extra : `${extra.name} x${extra.quantity}`;
};
